import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send/receive the httpOnly session cookies
});

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// Double-submit CSRF: the csrf_token cookie is deliberately readable by JS
// (unlike the session cookies) specifically so it can be echoed back here.
api.interceptors.request.use((config) => {
  const csrfToken = getCookie('csrf_token');
  if (csrfToken) config.headers['X-CSRF-Token'] = csrfToken;
  return config;
});

// The access token is short-lived (15 min) by design. On a 401, try a
// silent refresh once and retry the original request — this keeps a user
// logged in across a long session without them noticing, as long as the
// refresh token (30 days) is still valid. Concurrent 401s share one
// in-flight refresh instead of each firing their own.
let refreshPromise = null;
const AUTH_ENDPOINTS_EXEMPT_FROM_RETRY = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/2fa/verify'];

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const { config, response } = err;
    const isExempt = config && AUTH_ENDPOINTS_EXEMPT_FROM_RETRY.some((p) => config.url?.includes(p));

    if (response?.status === 401 && config && !config._retried && !isExempt) {
      config._retried = true;
      try {
        refreshPromise = refreshPromise || api.post('/auth/refresh');
        await refreshPromise;
        return api(config);
      } catch (refreshErr) {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshErr);
      } finally {
        refreshPromise = null;
      }
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login = (email, password) =>
  api.post('/auth/login', { email, password }).then((r) => r.data);

export const logout = () => api.post('/auth/logout').then((r) => r.data);

export const getMe = () => api.get('/auth/me').then((r) => r.data);

export const register = (email, password) =>
  api.post('/auth/register', { email, password }).then((r) => r.data);

export const verify2FA = (tempToken, code) =>
  api.post('/auth/2fa/verify', { tempToken, code }).then((r) => r.data);

export const setup2FA = (tempToken) =>
  api.post('/auth/2fa/setup', tempToken ? { tempToken } : {}).then((r) => r.data);

export const confirm2FA = (code, tempToken) =>
  api.post('/auth/2fa/confirm', tempToken ? { code, tempToken } : { code }).then((r) => r.data);

export const disable2FA = (password) =>
  api.post('/auth/2fa/disable', { password }).then((r) => r.data);

// ── App config ────────────────────────────────────────────────────────────────
export const getConfig = () => api.get('/config').then((r) => r.data);

// ── Admin ─────────────────────────────────────────────────────────────────────
export const getAdminSettings = () => api.get('/admin/settings').then((r) => r.data);

export const updateAdminSettings = (patch) => api.put('/admin/settings', patch).then((r) => r.data);

export const getAdminUsers = () => api.get('/admin/users').then((r) => r.data);

export const createAdminUser = (data) => api.post('/admin/users', data).then((r) => r.data);

export const unlockUser = (id) => api.post(`/admin/users/${id}/unlock`).then((r) => r.data);

// ── Receipts ──────────────────────────────────────────────────────────────────
export const getReceipts = (page = 1, limit = 20, filters = {}) =>
  api.get('/receipts', { params: { page, limit, ...filters } }).then((r) => r.data);

export const getReceipt = (id) => api.get(`/receipts/${id}`).then((r) => r.data);

export const updateReceiptStore = (id, patch) =>
  api.patch(`/receipts/${id}/store`, patch).then((r) => r.data);

export const createReceipt = (rawText) =>
  api.post('/receipts', { raw_text: rawText }).then((r) => r.data);

export const deleteReceipt = (id) => api.delete(`/receipts/${id}`).then((r) => r.data);

export const deleteReceipts = (ids) =>
  api.post('/receipts/bulk-delete', { ids }).then((r) => r.data);

// ── Categories ────────────────────────────────────────────────────────────────
export const getCategories = () => api.get('/categories').then((r) => r.data);

export const createCategory = (data) => api.post('/categories', data).then((r) => r.data);

// ── Stores ────────────────────────────────────────────────────────────────────
export const getStores = () => api.get('/stores').then((r) => r.data);

// ── Price observations ────────────────────────────────────────────────────────
export const getPriceObservations = (params) =>
  api.get('/price-observations', { params }).then((r) => r.data);

// ── Analytics ─────────────────────────────────────────────────────────────────
export const getInflation = () => api.get('/analytics/inflation').then((r) => r.data);

export const getStoreComparison = (categoryId) =>
  api.get('/analytics/store-comparison', { params: { category_id: categoryId } }).then((r) => r.data);

export const getStoreAnalytics = (from, to) =>
  api.get('/analytics/stores', { params: { from, to } }).then((r) => r.data);

export const createReceiptFromImage = (file) => {
  const form = new FormData();
  form.append('image', file);
  return api.post('/receipts', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then((r) => r.data);
};

export default api;
